import { personPrincipalPersonFuldmForm } from "../../../../../config/form/person/personPrincipalFulmFormConfig";
import {
  defaultPersonPrincipalPersonFuldmColumns,
  defaultPersonPrincipalPersonPrincipalNavigationColumns,
} from "../../../../../config/person";
import {
  createColumnEditorTrigger,
  getHeaderActiveColumns,
  TD,
} from "../../../../../util/table";
import { tableToXLSX } from "../../../../../util/tableExportUtils";
import ButtonDownload from "../../../../ButtonDownload";
import ActiveIcon from "../../../../icons/ActiveIcon";
import { ModalProvider } from "../../../../Modal/Modal";
import {
  TableBody,
  TableHead,
  TableRoot,
  TableRow,
} from "../../../../Table/TableBasic";
import ColumnEditorModal from "@/components/column-editor/ColumnEditorModal";
import IngenRegistreringer from "../IngenRegistreringer";
import OpretNyButtonBlock from "../OpretNyButtonBlock";
import { PersonDto } from "../../../../../interfaces/schema";
import {
  PersonPrincipalPersonFuldmField,
  PersonPrincipalPersonPrincipalNavigationField,
} from "../../../../../interfaces/personer";
import { SortBy, IColumn } from "../../../../../interfaces/table";
import useLocalStorageState from "use-local-storage-state";
import { useContext } from "react";
import { UserQueryContext } from "../../../../../context/UserContext";
import { extractCurrentPersonArt } from "../../../../../util/person";
import { periodeIsActive } from "../../../../../util/date";
import { orderBy } from "../../../../../util/orderBy";

const defaultSortBy: SortBy[] = [
  {
    direction: "desc",
    field: "periode.tilDatoInklusiv",
  },
  {
    direction: "desc",
    field: "periode.fraDato",
  },
];

export default function PersonRelationer({
  person,
  personPrincipalPersonFuldmColumns,
  personPrincipalPersonPrincipalNavigationColumns,
  setPersonPrincipalPersonFuldmActiveColumns,
  setPersonPrincipalPersonPrincipalNavigationActiveColumns,
  invalidateData,
}: {
  person: PersonDto;
  invalidateData: InvalidateDataFunction<any>;
  personPrincipalPersonPrincipalNavigationColumns: IColumn<PersonPrincipalPersonPrincipalNavigationField>[];
  setPersonPrincipalPersonPrincipalNavigationActiveColumns: React.Dispatch<
    React.SetStateAction<PersonPrincipalPersonPrincipalNavigationField[]>
  >;
  personPrincipalPersonFuldmColumns: IColumn<PersonPrincipalPersonFuldmField>[];
  setPersonPrincipalPersonFuldmActiveColumns: React.Dispatch<
    React.SetStateAction<PersonPrincipalPersonFuldmField[]>
  >;
}) {
  return (
    <div>
      <div className="c-card px-3 pt-2 pb-4 mb-4">
        <h5 className="my-2 badge">Principal for</h5>
        <PrincipalFor
          person={person}
          invalidateData={invalidateData}
          personPrincipalPersonPrincipalNavigationColumns={
            personPrincipalPersonPrincipalNavigationColumns
          }
          setPersonPrincipalPersonPrincipalNavigationActiveColumns={
            setPersonPrincipalPersonPrincipalNavigationActiveColumns
          }
        />
      </div>

      <div className="c-card px-3 pt-2 pb-4 mb-4">
        <h5 className="my-2 badge">Fuldmægtig under</h5>
        <FullmaegtigUnder
          person={person}
          invalidateData={invalidateData}
          personPrincipalPersonFuldmColumns={personPrincipalPersonFuldmColumns}
          setPersonPrincipalPersonFuldmActiveColumns={
            setPersonPrincipalPersonFuldmActiveColumns
          }
        />
      </div>
    </div>
  );
}

function PrincipalFor({
  invalidateData,
  person,
  personPrincipalPersonPrincipalNavigationColumns,
  setPersonPrincipalPersonPrincipalNavigationActiveColumns,
}: {
  person: PersonDto;
  personPrincipalPersonPrincipalNavigationColumns: IColumn<PersonPrincipalPersonPrincipalNavigationField>[];
  setPersonPrincipalPersonPrincipalNavigationActiveColumns: React.Dispatch<
    React.SetStateAction<PersonPrincipalPersonPrincipalNavigationField[]>
  >;
  invalidateData: InvalidateDataFunction<any>;
}) {
  const [sortBy, setSortBy] = useLocalStorageState<SortBy[]>(
    "personPrincipalForSortBy",
    {
      defaultValue: defaultSortBy,
    },
  );

  if (person.personPrincipalPersonPrincipalNavigation?.length === 0) {
    return (
      <IngenRegistreringer
        text="Ingen fuldmægtige registreret"
        invalidateData={invalidateData}
      />
    );
  }

  // event handlers
  function downloadHandler() {
    tableToXLSX(
      personPrincipalPersonPrincipalNavigationColumns,
      person.personPrincipalPersonPrincipalNavigation ?? [],
      {
        fileName: `principal-for-${person.kundeNr}`,
        additionalData: {
          kundenr: person.kundeNr,
        },
      },
    );
  }

  return (
    <ModalProvider>
      <ButtonDownload onClick={downloadHandler} />
      <TableRoot>
        <TableHead ColumnEditorTrigger={createColumnEditorTrigger()}>
          <>
            <th className="indicator"></th>
            {getHeaderActiveColumns(
              personPrincipalPersonPrincipalNavigationColumns,
              {
                defaultSortBy,
                setSortBy,
                sortBy,
              },
            )}
          </>
        </TableHead>
        <TableBody>
          {orderBy(
            [...(person.personPrincipalPersonPrincipalNavigation ?? [])],
            sortBy.map((s) => s.field),
            sortBy.map((s) => s.direction),
          ).map((f) => {
            return (
              <TableRow key={f.id} invalidateData={invalidateData}>
                <td
                  className={`indicator${
                    periodeIsActive(
                      new Date(f.periode.fraDato),
                      new Date(f.periode.tilDatoInklusiv),
                    )
                      ? " has-effect"
                      : ""
                  }`}
                >
                  <ActiveIcon />
                </td>
                <>
                  {personPrincipalPersonPrincipalNavigationColumns
                    .filter((col) => col.active)
                    .map((col) => {
                      return <TD key={col.field} col={col} rowData={f} />;
                    })}
                </>
              </TableRow>
            );
          })}
        </TableBody>
      </TableRoot>
      <ColumnEditorModal
        defaultColumns={defaultPersonPrincipalPersonPrincipalNavigationColumns}
        columns={personPrincipalPersonPrincipalNavigationColumns}
        setActiveColumns={
          setPersonPrincipalPersonPrincipalNavigationActiveColumns
        }
      />
    </ModalProvider>
  );
}

function FullmaegtigUnder({
  invalidateData,
  person,
  personPrincipalPersonFuldmColumns,
  setPersonPrincipalPersonFuldmActiveColumns,
}: {
  person: PersonDto;
  personPrincipalPersonFuldmColumns: IColumn<PersonPrincipalPersonFuldmField>[];
  setPersonPrincipalPersonFuldmActiveColumns: React.Dispatch<
    React.SetStateAction<PersonPrincipalPersonFuldmField[]>
  >;
  invalidateData: InvalidateDataFunction<any>;
}) {
  const { user, hasPermission } = useContext(UserQueryContext);
  const isAuth = hasPermission(user, "BasisWrite");
  const [sortBy, setSortBy] = useLocalStorageState<SortBy[]>(
    "personFullmaegtigUnderSortBy",
    {
      defaultValue: defaultSortBy,
    },
  );

  // event handlers
  function downloadHandler() {
    tableToXLSX(
      personPrincipalPersonFuldmColumns,
      person.personPrincipalPersonFuldm ?? [],
      {
        fileName: `fuldmægtig-under-${person.kundeNr}`,
        additionalData: {
          kundenr: person.kundeNr,
        },
      },
    );
  }

  const personArt = extractCurrentPersonArt(person);
  const isOevrig = personArt?.personartNrNavigation?.nr === "OEVRIG";
  const isAdvokat = personArt?.personartNrNavigation?.nr === "ADVOKAT";
  const buttonInactive =
    isOevrig && person.personPrincipalPersonFuldm?.length === 0;

  if (person.personPrincipalPersonFuldm?.length === 0) {
    return (
      <IngenRegistreringer
        text="Ingen principaler registreret"
        invalidateData={invalidateData}
        editConfig={
          isAuth && !buttonInactive && !isAdvokat
            ? personPrincipalPersonFuldmForm(person.id, undefined)
            : undefined
        }
      />
    );
  }

  return (
    <>
      <ModalProvider>
        <ButtonDownload onClick={downloadHandler} />
        <TableRoot>
          <TableHead ColumnEditorTrigger={createColumnEditorTrigger()}>
            <>
              <th className="indicator"></th>
              {getHeaderActiveColumns(personPrincipalPersonFuldmColumns, {
                defaultSortBy,
                setSortBy,
                sortBy,
              })}
            </>
          </TableHead>
          <TableBody>
            {orderBy(
              [...(person.personPrincipalPersonFuldm ?? [])],
              sortBy.map((s) => s.field),
              sortBy.map((s) => s.direction),
            ).map((principal) => {
              return (
                <TableRow
                  key={principal.id}
                  editConfig={
                    isAuth
                      ? personPrincipalPersonFuldmForm(person.id, principal.id)
                      : undefined
                  }
                  invalidateData={invalidateData}
                >
                  <td
                    className={`indicator${
                      periodeIsActive(
                        new Date(principal.periode.fraDato),
                        new Date(principal.periode.tilDatoInklusiv),
                      )
                        ? " has-effect"
                        : ""
                    }`}
                  >
                    <ActiveIcon />
                  </td>
                  <>
                    {personPrincipalPersonFuldmColumns
                      .filter((col) => col.active)
                      .map((col) => {
                        return (
                          <TD key={col.field} col={col} rowData={principal} />
                        );
                      })}
                  </>
                </TableRow>
              );
            })}
          </TableBody>
        </TableRoot>
        <ColumnEditorModal
          defaultColumns={defaultPersonPrincipalPersonFuldmColumns}
          columns={personPrincipalPersonFuldmColumns}
          setActiveColumns={setPersonPrincipalPersonFuldmActiveColumns}
        />
        <OpretNyButtonBlock
          invalidateData={invalidateData}
          editConfig={
            isAuth && !isAdvokat
              ? personPrincipalPersonFuldmForm(person.id, undefined)
              : undefined
          }
        />
      </ModalProvider>
    </>
  );
}
